// Sensor coming-soon page — /sensor
// Sections: nav, hero, what-it-does, specs, waitlist form, FAQ, footer.

import { useState } from "react";
import { Link } from "react-router-dom";
import { useMediaQuery } from "../lib/useMediaQuery";
import { useCurrentUser } from "../lib/useCurrentUser";
import { submitWaitlist } from "../lib/waitlist";
import "../styles/landing.css";
import "../styles/sensor.css";

const SnIc = {
  drop: <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M7 1.5C 4.5 5, 3 6.5, 3 9 a4 4 0 0 0 8 0 c 0 -2.5 -1.5 -4 -4 -7.5z" fill="currentColor" /></svg>,
  arrow: <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M3 7H11M8 4L11 7L8 10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>,
  plus: <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M7 3V11M3 7H11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /></svg>,
  chev: <svg width="12" height="12" viewBox="0 0 14 14" fill="none"><path d="M4 5L7 8L10 5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" /></svg>,
  measure: <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M3 18h18M3 18V12M3 18l4-4M21 18V8M21 18l-4-4M9 18v-4l4-3 4 3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" /></svg>,
  cloud: <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M7 18a5 5 0 1 1 1.5-9.8A6 6 0 0 1 20 11a4 4 0 0 1-1 7H7z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" /><path d="M12 14v4M10 16h4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" /></svg>,
  signal: <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M5 14a8 8 0 0 1 14 0M8 17a4 4 0 0 1 8 0" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" /><circle cx="12" cy="19.5" r="1.5" fill="currentColor" /></svg>,
};

function SnNav() {
  return (
    <nav className="snr-nav">
      <div className="snr-nav-inner">
        <Link to="/" className="lp-wordmark">
          <img src="/logo-wordmark.png" alt="Troughmate" height={28} style={{ display: "block", width: "auto" }} />
        </Link>
        <div className="lp-nav-links">
          <Link to="/" className="lp-nav-link">The app</Link>
          <a href="#specs" className="lp-nav-link">Specs</a>
          <a href="#faq" className="lp-nav-link">FAQ</a>
          <a href="#waitlist" className="lp-nav-link lp-nav-cta">Join →</a>
        </div>
      </div>
    </nav>
  );
}

function SnHero() {
  return (
    <section className="snr-hero">
      <div className="snr-container">
        <div className="snr-hero-grid">
          <div className="snr-hero-copy">
            <span className="snr-eyebrow">
              <span className="snr-eyebrow-dot"></span>
              Coming soon · 2026
            </span>
            <p className="snr-hero-name">Troughmate sensor</p>
            <h1 className="snr-hero-headline">
              Hydration,<br />on <em>autopilot.</em>
            </h1>
            <p className="snr-hero-sub">
              The Troughmate sensor measures every drink, every horse, automatically.
              Pairs with your app. No manual entry, ever again.
            </p>
            <div className="snr-hero-stats">
              <div><span className="snr-hero-stat-num">±2%</span> volume accuracy</div>
              <div><span className="snr-hero-stat-num">8h</span> upload interval</div>
              <div><span className="snr-hero-stat-num">10 yr</span> battery</div>
            </div>
          </div>

          <div className="snr-viz">
            <span className="snr-viz-tag">Troughmate sensor</span>
            <span className="snr-viz-ping">live · 0.4 L/min</span>
            <div className="snr-trough">
              <div className="snr-water"></div>
            </div>
            <div className="snr-device">
              <span className="snr-device-led"></span>
              <span className="snr-device-label">The puck</span>
            </div>
            <div className="snr-viz-readout">
              <span className="snr-viz-readout-label">Henry · drink in progress</span>
              <span className="snr-viz-readout-val">3.6 L · 9 min</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function SnWhat() {
  return (
    <section className="snr-what">
      <div className="snr-container">
        <header className="snr-section-head">
          <div className="snr-section-num">01 / What it does</div>
          <h2 className="snr-section-title">Every drink. Every horse. Automatically.</h2>
          <p className="snr-section-sub">
            One puck per trough. Bolt it on, walk away. The app gets the data, the staff get the morning back.
          </p>
        </header>
        <div className="snr-feat-row">
          <article className="snr-feat">
            <span className="snr-feat-icon">{SnIc.measure}</span>
            <h3 className="snr-feat-title">Measures litres per horse, per drink.</h3>
            <p className="snr-feat-body">
              Acoustic time-of-flight sensor reads water level. Drinks are attributed by RFID tag on the headcollar.
            </p>
            <span className="snr-feat-pill">±2% accuracy</span>
          </article>
          <article className="snr-feat">
            <span className="snr-feat-icon">{SnIc.cloud}</span>
            <h3 className="snr-feat-title">Sends to your app every 8 hours.</h3>
            <p className="snr-feat-body">
              Same dashboard you already use. Hydration status updates automatically. No log button, no daily reminders.
            </p>
            <span className="snr-feat-pill">Zero logging</span>
          </article>
          <article className="snr-feat">
            <span className="snr-feat-icon">{SnIc.signal}</span>
            <h3 className="snr-feat-title">Works on any UK yard with NB-IoT coverage.</h3>
            <p className="snr-feat-body">
              That's most of the country, including rural yards with no WiFi. We'll check your postcode before shipping.
            </p>
            <span className="snr-feat-pill">No WiFi needed</span>
          </article>
        </div>
      </div>
    </section>
  );
}

function SnSpecs() {
  return (
    <section className="snr-specs" id="specs">
      <div className="snr-container">
        <header className="snr-section-head">
          <div className="snr-section-num">02 / The hardware</div>
          <h2 className="snr-section-title">Built for the British yard.</h2>
          <p className="snr-section-sub">
            Stainless rim mount, IP67 sealed, frost-tested to −15°C. Designed to outlive the horse.
          </p>
        </header>
        <div className="snr-spec-grid">
          <div className="snr-spec">
            <span className="snr-spec-label">Battery life</span>
            <span className="snr-spec-val">10<span className="snr-spec-unit">yrs</span></span>
            <span className="snr-spec-note">Replaceable CR123A pack.</span>
          </div>
          <div className="snr-spec">
            <span className="snr-spec-label">Ingress rating</span>
            <span className="snr-spec-val">IP67</span>
            <span className="snr-spec-note">Sealed against mud, rain, splash.</span>
          </div>
          <div className="snr-spec">
            <span className="snr-spec-label">Trough size</span>
            <span className="snr-spec-val">Any</span>
            <span className="snr-spec-note">Field, stable, auto-filler.</span>
          </div>
        </div>
      </div>
    </section>
  );
}

type FormState = "default" | "submitting" | "success" | "error";

function SnForm() {
  const [state, setState] = useState<FormState>("default");
  const [position, setPosition] = useState<number | null>(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [yardName, setYardName] = useState("");
  const [horses, setHorses] = useState("");
  const [timing, setTiming] = useState("6mo");
  const { user } = useCurrentUser();

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setState("submitting");
    try {
      const horseCount = horses ? parseInt(horses, 10) : undefined;
      const result = await submitWaitlist({
        email,
        name,
        yardName,
        horseCount,
        timing,
        source: "sensor-page",
        userId: user?.uid,
      });
      setPosition(result.position);
      setState("success");
    } catch {
      setState("error");
    }
  };

  if (state === "success") {
    return (
      <section className="snr-form-section" id="waitlist">
        <div className="snr-container">
          <div className="snr-form-card">
            <div className="snr-success">
              <span className="snr-success-icon">
                <svg width="28" height="28" viewBox="0 0 14 14" fill="none">
                  <path d="M3 7.5L6 10L11 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </span>
              <h2 className="snr-success-title">You're on the list.</h2>
              <p className="snr-success-sub">
                We'll email you the moment we open the first batch, and again before, when there's a beta-yard window in your region.
              </p>
              <span className="snr-success-pill">
                Position <strong>#{position ?? "—"}</strong>
              </span>

              <div className="snr-success-next">
                <div className="snr-success-next-text">
                  <strong>While you wait:</strong> the app is free and ready to use today.
                  Start logging now and the sensor will pick up where you left off.
                </div>
                <Link to="/signup" className="snr-cta">Start the app free {SnIc.arrow}</Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  const isLoading = state === "submitting";
  return (
    <section className="snr-form-section" id="waitlist">
      <div className="snr-container">
        <form className="snr-form-card" onSubmit={submit}>
          <div className="snr-form-head">
            <div className="snr-section-num">03 / Join the waitlist</div>
            <h2>Get early access, and early-access pricing.</h2>
            <p>
              Beta yards get the first units at cost. We're shipping the first batch
              in autumn 2026 to UK yards with NB-IoT coverage.
            </p>
          </div>

          <div className="snr-form-fields">
            <div className="snr-form-field">
              <label className="snr-form-label">Your name</label>
              <input
                className="snr-form-input"
                placeholder="e.g. Sarah Hollins"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            <div className="snr-form-field">
              <label className="snr-form-label">Email</label>
              <input
                className="snr-form-input"
                type="email"
                placeholder="you@yardname.co.uk"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div className="snr-form-field">
              <label className="snr-form-label">Yard name</label>
              <input
                className="snr-form-input"
                placeholder="e.g. Hollow Tree Livery"
                required
                value={yardName}
                onChange={(e) => setYardName(e.target.value)}
              />
            </div>
            <div className="snr-form-field">
              <label className="snr-form-label">
                Horses on yard <span className="opt">approx.</span>
              </label>
              <input
                className="snr-form-input"
                placeholder="12"
                inputMode="numeric"
                value={horses}
                onChange={(e) => setHorses(e.target.value.replace(/[^0-9]/g, ""))}
              />
            </div>
            <div className="snr-form-field full">
              <label className="snr-form-label">When are you looking to install?</label>
              <div className="snr-form-select-wrap">
                <select
                  className="snr-form-select"
                  value={timing}
                  onChange={(e) => setTiming(e.target.value)}
                >
                  <option value="curious">Just curious, keep me posted</option>
                  <option value="6mo">Interested, within 6 months</option>
                  <option value="now">Want it now, first batch please</option>
                </select>
                <span className="snr-form-select-chev">{SnIc.chev}</span>
              </div>
            </div>
          </div>

          <div className="snr-form-foot">
            <p className="snr-form-meta">
              {state === "error"
                ? "Couldn't submit. Check your connection and try again."
                : <>We'll only email you about the sensor. No marketing list. See our <Link to="/privacy">privacy policy</Link>.</>}
            </p>
            <button className={`snr-cta ${isLoading ? "is-loading" : ""}`} type="submit" disabled={isLoading}>
              {isLoading
                ? <><span className="snr-spinner"></span> Joining…</>
                : <>Join the waitlist {SnIc.arrow}</>}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}

interface FaqItem {
  q: string;
  a: string;
}

const FAQ_DATA: FaqItem[] = [
  {
    q: "How much will it cost?",
    a: "Pricing isn't finalised yet. Waitlist yards will get early-access pricing when we open orders. We'll share the full numbers as soon as they're locked in.",
  },
  {
    q: "How does installation work?",
    a: "Two screws on the back of a trough or the inside of a stable. The puck self-calibrates over the first 24 hours of use. We'll ship with a calibration guide and a fitting video. Most yards have a sensor on every trough in under an hour.",
  },
  {
    q: "How accurate is the measurement?",
    a: "±2% on volume per drink. Per-horse attribution requires an RFID tag on the headcollar (included). Without RFID, the sensor still reports aggregate trough intake, useful for single-horse paddocks.",
  },
  {
    q: "Will it work on my yard?",
    a: "If you have NB-IoT coverage (Vodafone or O2, most of the UK), yes. We'll verify your postcode before shipping. Coverage is much better than 4G in rural areas. Most yards that have no WiFi still have it.",
  },
  {
    q: "When will it ship?",
    a: "First batch: autumn 2026. Beta yards in spring 2026 (limited to ~20 yards across the UK, waitlist priority by yard size and urgency).",
  },
];

function FaqItemRow({ q, a, defaultOpen = false }: FaqItem & { defaultOpen?: boolean }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className={`snr-faq-item ${open ? "open" : ""}`}>
      <button className="snr-faq-q" onClick={() => setOpen(!open)} type="button">
        <span>{q}</span>
        <span className="snr-faq-icon">{SnIc.plus}</span>
      </button>
      <div className="snr-faq-a">{a}</div>
    </div>
  );
}

function SnFAQ() {
  return (
    <section className="snr-faq" id="faq">
      <div className="snr-container">
        <header className="snr-section-head">
          <div className="snr-section-num">04 / Common questions</div>
          <h2 className="snr-section-title">FAQ</h2>
        </header>
        <div className="snr-faq-list">
          {FAQ_DATA.map((item, i) => (
            <FaqItemRow key={i} q={item.q} a={item.a} defaultOpen={i === 0} />
          ))}
        </div>
      </div>
    </section>
  );
}

function SnFooter() {
  return (
    <footer className="snr-footer">
      <div className="snr-container">
        <div className="snr-footer-grid">
          <Link to="/" className="lp-wordmark">
            <img src="/logo-wordmark.png" alt="Troughmate" height={24} style={{ display: "block", width: "auto" }} />
          </Link>
          <div className="snr-footer-links">
            <Link to="/">The app</Link>
            <a href="#specs">Specs</a>
            <a href="#faq">FAQ</a>
            <Link to="/privacy">Privacy</Link>
            <Link to="/terms">Terms</Link>
            <a href="mailto:hello@troughmate.com">Contact</a>
          </div>
        </div>
        <div className="snr-footer-fineprint">
          © 2026 Troughmate, What Does The Horse Drink Ltd. The sensor is in development. Specs and timing may change before launch.
        </div>
      </div>
    </footer>
  );
}

export default function Sensor() {
  const isMobile = useMediaQuery("(max-width: 600px)");
  return (
    <div className={`snr ${isMobile ? "is-mobile" : "is-desktop"}`}>
      <SnNav />
      <SnHero />
      <SnWhat />
      <SnSpecs />
      <SnForm />
      <SnFAQ />
      <SnFooter />
    </div>
  );
}
